import React from 'react'
import { withTranslation } from 'react-i18next'
import { authContext } from '../../../contexts/AuthContext'
import { listExpirationsByBrandLabel } from '../../../services/calendar'
import {
  listClientsByUser,
  getMainClient,
  listAuthorizedClientsByClientUser
} from '../../../services/clients'
import { RRule } from 'rrule'
import TemplatePreview from './TemplatePreview'
import {
  getDocAlertEvents,
  getDocAlertEventsByClient,
  getExpirationsByClient,
  getReminders,
  getTemplateById
} from '../../../services/notificationRules'

// ─── Timezone-safe date parser ───────────────────────────────────────────────
// Parses "yyyy-MM-dd" as LOCAL midnight — avoids UTC offset shifting dates by 1
function parseLocalDate(str) {
  if (!str) return null
  const m = String(str).match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (m) return new Date(+m[1], +m[2] - 1, +m[3])
  // fallback for ISO datetime strings — strip Z to treat as local
  return new Date(String(str).replace('Z', ''))
}

function dateKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function buildEventMap(events) {
  const map = {}
  events.forEach((ev) => {
    const d = parseLocalDate(ev.expiration)
    if (!d) return
    const k = dateKey(d)
    if (!map[k]) map[k] = []
    map[k].push(ev)
  })
  return map
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
]
const DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

// ─── Styles ──────────────────────────────────────────────────────────────────
const S = {
  // Layout
  wrap: {
    fontFamily: "'Georgia', serif",
    width: '100%',
    maxWidth: 960,
    margin: '0 auto',
    padding: '1rem 0.5rem 3rem',
    boxSizing: 'border-box'
  },
  // Header
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '1.25rem',
    flexWrap: 'wrap',
    gap: '0.75rem'
  },
  monthLabel: {
    fontSize: 26,
    fontWeight: 400,
    letterSpacing: '-0.5px',
    color: '#000',
    fontFamily: "'Georgia', serif"
  },
  yearSpan: {
    color: '#000',
    fontWeight: 400
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap'
  },
  // View toggle
  viewToggle: {
    display: 'flex',
    border: '0.5px solid #333',
    borderRadius: 8,
    overflow: 'hidden'
  },
  viewBtn: (active) => ({
    padding: '6px 14px',
    fontSize: 12,
    fontWeight: 500,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    border: 'none',
    cursor: 'pointer',
    background: active ? '#102a46' : '#ffffff',
    color: active ? '#ffffff' : '#333',
    transition: 'background 0.15s, color 0.15s'
  }),
  // Nav buttons
  navGroup: { display: 'flex', alignItems: 'center', gap: 6 },
  navBtn: {
    background: '#fff',
    border: '0.5px solid #102a46',
    color: '#333',
    width: 32,
    height: 32,
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  todayBtn: {
    padding: '0 12px',
    height: 32,
    fontSize: 12,
    fontWeight: 500,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    background: '#fff',
    border: '0.5px solid #102a46',
    color: '#333',
    borderRadius: 8,
    cursor: 'pointer'
  },
  // Legend
  legend: {
    display: 'flex',
    gap: 16,
    alignItems: 'center',
    marginBottom: '1rem',
    flexWrap: 'wrap'
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    color: '#333'
  },
  legendDot: (color) => ({
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: color,
    flexShrink: 0
  }),
  // Weekday row
  weekdayRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    marginBottom: 4
  },
  weekdayCell: {
    textAlign: 'center',
    fontSize: 10,
    fontWeight: 500,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#333',
    padding: '4px 0'
  },
  // Calendar grid
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: 3
  },
  // Day cell
  dayCell: (isCurrentMonth, isToday, hasEvents) => ({
    minHeight: 90,
    borderRadius: 8,
    border: isToday ? '1.5px solid #534AB7' : '0.5px solid #333',
    background: isToday ? '#EEEDFE' : isCurrentMonth ? '#fff' : '#ccc',
    padding: '6px 5px',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    cursor: hasEvents ? 'pointer' : 'default',
    opacity: isCurrentMonth ? 1 : 0.45,
    transition: 'border-color 0.12s',
    boxSizing: 'border-box',
    overflow: 'hidden'
  }),
  dayNum: (isToday) => ({
    fontSize: 12,
    fontWeight: isToday ? 600 : 400,
    color: isToday ? '#085041' : '#333',
    lineHeight: 1,
    marginBottom: 2,
    fontFamily: "'Georgia', serif"
  }),
  pill: (type) => ({
    fontSize: 10,
    padding: '2px 5px',
    borderRadius: 4,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: 1.5,
    background:
      type === 'expiration'
        ? '#FCEBEB'
        : type === 'reminder'
          ? '#E1F5EE'
          : '#E6F1FB',
    color:
      type === 'expiration'
        ? '#791F1F'
        : type === 'reminder'
          ? '#085041'
          : '#0C447C'
  }),
  moreBadge: {
    fontSize: 10,
    color: '#333',
    paddingLeft: 2,
    marginTop: 1
  },
  dotRow: {
    display: 'flex',
    gap: 3,
    marginTop: 2,
    flexWrap: 'wrap'
  },
  dot: (type) => ({
    width: 6,
    height: 6,
    borderRadius: '50%',
    background:
      type === 'expiration'
        ? '#A32D2D'
        : type === 'reminder'
          ? '#0F6E56'
          : '#185FA5',
    flexShrink: 0
  }),

  // ── LIST VIEW ──────────────────────────────────────────────────────────────
  listWrap: { display: 'flex', flexDirection: 'column', gap: 0 },
  listDayGroup: {
    borderBottom: '0.5px solid #333',
    paddingBottom: '0.75rem',
    marginBottom: '0.75rem'
  },
  listDayHeader: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    marginBottom: '0.5rem',
    flexWrap: 'wrap',
    gap: 8
  },
  listDayTitle: {
    fontSize: 14,
    fontWeight: 500,
    color: '#333',
    fontFamily: "'Georgia', serif"
  },
  listSortBtn: {
    fontSize: 11,
    color: '#333',
    background: 'none',
    border: '0.5px solid #333',
    borderRadius: 6,
    padding: '2px 8px',
    cursor: 'pointer',
    letterSpacing: '0.03em'
  },
  listEventRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '7px 10px',
    borderRadius: 8,
    border: '0.5px solid #333',
    marginBottom: 4,
    cursor: 'pointer',
    background: '#fff',
    transition: 'border-color 0.12s'
  },
  listEventBar: (type) => ({
    width: 3,
    height: 32,
    borderRadius: 2,
    background:
      type === 'expiration'
        ? '#A32D2D'
        : type === 'reminder'
          ? '#0F6E56'
          : '#185FA5',
    flexShrink: 0
  }),
  listEventInfo: { flex: 1, overflow: 'hidden' },
  listEventName: {
    fontSize: 13,
    fontWeight: 500,
    color: '#333',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  listEventClient: {
    fontSize: 11,
    color: '#333'
  },
  listEventType: (type) => ({
    fontSize: 10,
    fontWeight: 500,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color:
      type === 'expiration'
        ? '#791F1F'
        : type === 'reminder'
          ? '#085041'
          : '#0C447C',
    background:
      type === 'expiration'
        ? '#FCEBEB'
        : type === 'reminder'
          ? '#E1F5EE'
          : '#E6F1FB',
    borderRadius: 4,
    padding: '2px 6px',
    flexShrink: 0
  }),

  // ── PANEL (day event list) ─────────────────────────────────────────────────
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.32)',
    zIndex: 200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem'
  },
  panel: {
    background: '#fff',
    borderRadius: 12,
    border: '0.5px solid #333',
    width: '100%',
    maxWidth: 440,
    maxHeight: '82vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box'
  },
  panelHead: {
    padding: '1rem 1.25rem 0.75rem',
    borderBottom: '0.5px solid #333',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0
  },
  panelTitle: {
    fontSize: 16,
    fontWeight: 400,
    fontFamily: "'Georgia', serif",
    color: '#333'
  },
  panelClose: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#333',
    fontSize: 18,
    lineHeight: 1,
    padding: '2px 4px'
  },
  panelSortRow: {
    padding: '0.5rem 1.25rem',
    borderBottom: '0.5px solid #333',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0
  },
  sortLabel: {
    fontSize: 11,
    color: '#333',
    letterSpacing: '0.04em',
    textTransform: 'uppercase'
  },
  sortChip: (active) => ({
    fontSize: 11,
    padding: '3px 10px',
    borderRadius: 20,
    border: '0.5px solid #333',
    background: active ? '#333' : 'transparent',
    color: active ? '#fff' : '#333',
    cursor: 'pointer',
    fontWeight: active ? 500 : 400
  }),
  panelBody: {
    overflowY: 'auto',
    padding: '0.75rem 1.25rem',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    flex: 1
  },

  // Event list item in panel
  eventListItem: {
    borderRadius: 8,
    border: '0.5px solid #333',
    padding: '10px 12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: '#fff',
    transition: 'border-color 0.12s'
  },
  eventListBar: (type) => ({
    width: 3,
    minHeight: 36,
    alignSelf: 'stretch',
    borderRadius: 2,
    background:
      type === 'expiration'
        ? '#A32D2D'
        : type === 'reminder'
          ? '#0F6E56'
          : '#185FA5',
    flexShrink: 0
  }),
  eventListContent: { flex: 1, overflow: 'hidden' },
  eventListType: (type) => ({
    fontSize: 10,
    letterSpacing: '0.07em',
    textTransform: 'uppercase',
    fontWeight: 500,
    color:
      type === 'expiration'
        ? '#A32D2D'
        : type === 'reminder'
          ? '#0F6E56'
          : '#185FA5',
    marginBottom: 2
  }),
  eventListName: {
    fontSize: 13,
    fontWeight: 500,
    color: '#333',
    marginBottom: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  eventListClient: { fontSize: 11, color: '#333' },
  eventListChevron: {
    color: '#333',
    fontSize: 14,
    flexShrink: 0
  },

  // ── DETAIL PANEL ───────────────────────────────────────────────────────────
  detailPanel: {
    background: '#fff',
    borderRadius: 12,
    border: '0.5px solid #333',
    width: '100%',
    maxWidth: 440,
    maxHeight: '82vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box'
  },
  detailHead: {
    padding: '1rem 1.25rem 0.75rem',
    borderBottom: '0.5px solid #333',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexShrink: 0
  },
  backBtn: {
    background: 'none',
    border: '0.5px solid #333',
    borderRadius: 6,
    cursor: 'pointer',
    color: '#333',
    fontSize: 13,
    padding: '4px 10px',
    display: 'flex',
    alignItems: 'center',
    gap: 4
  },
  detailBody: {
    overflowY: 'auto',
    padding: '1.25rem',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 0
  },
  detailTypeBadge: (type) => ({
    display: 'inline-block',
    fontSize: 10,
    fontWeight: 500,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color:
      type === 'expiration'
        ? '#791F1F'
        : type === 'reminder'
          ? '#085041'
          : '#0C447C',
    background:
      type === 'expiration'
        ? '#FCEBEB'
        : type === 'reminder'
          ? '#D0F0E0'
          : '#E6F1FB',
    borderRadius: 4,
    padding: '3px 8px',
    marginBottom: 10
  }),
  detailTitle: {
    fontSize: 18,
    fontWeight: 400,
    fontFamily: "'Georgia', serif",
    color: '#333',
    marginBottom: '1rem',
    lineHeight: 1.4
  },
  detailRow: {
    display: 'flex',
    padding: '10px 0',
    borderBottom: '0.5px solid #333',
    gap: 8
  },
  detailLabel: {
    fontSize: 11,
    color: '#333',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    minWidth: 90,
    paddingTop: 1
  },
  detailValue: {
    fontSize: 13,
    color: '#333',
    flex: 1
  },
  goDocBtn: {
    marginTop: '1.5rem',
    padding: '10px 0',
    width: '100%',
    background: '#102a46',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    letterSpacing: '0.03em'
  }
}

// Takes raw reminders from GetReminders and expands them into
// flat dated events that match the shape your eventMap expects.
// windowStart / windowEnd let you limit how far ahead you expand.
export function expandReminders(reminders, windowStart, windowEnd) {
  const events = []

  reminders.forEach((reminder) => {
    if (!reminder.is_active) return

    const base = {
      filename: reminder.description,
      name: null, // reminders have no client
      type: 'reminder',
      rule_id: reminder.rule_id,
      template_id: reminder.template_id,
      doc_id: null, // no document link on scheduled reminders
      client_id: null
    }

    if (reminder.rrule) {
      try {
        const parsedRule = RRule.fromString(reminder.rrule)

        // Parse starts_at as local date, strip time component
        const startsAt = parseLocalDate(reminder.starts_at)

        const rule = new RRule({
          ...parsedRule.origOptions,
          dtstart: startsAt // anchor recurrence to local start date
        })

        const start = parseLocalDate(reminder.starts_at)
        const end = windowEnd || new Date(start.getFullYear() + 2, 0, 1)
        const occurrences = rule
          .all()
          .filter((date) => date >= start && date <= end)

        occurrences.forEach((date) => {
          events.push({
            ...base,
            expiration: dateKey(date),
            rrule: reminder.rrule,
            recurrence_end: reminder.recurrence_end,
            template_id: reminder.template_id,
            has_en: reminder.has_en,
            has_es: reminder.has_es
          })
        })
      } catch (e) {
        console.warn('Invalid rrule on reminder', reminder.rule_id, e)
      }
    } else if (reminder.starts_at) {
      // Fixed date — single occurrence
      events.push({
        ...base,
        expiration: reminder.starts_at,
        rrule: reminder.rrule,
        recurrence_end: reminder.recurrence_end,
        template_id: reminder.template_id,
        has_en: reminder.has_en,
        has_es: reminder.has_es
      })
    }
  })

  return events
}

export function expandAlerts(alerts, windowEnd) {
  const events = []

  alerts.forEach((alert) => {
    const base = {
      filename: alert.filename, // document name
      name: alert.client_name, // client name
      type: 'alert',
      rule_id: alert.rule_id,
      template_id: alert.template_id,
      doc_id: alert.doc_id,
      client_id: alert.client_id,
      has_en: alert.has_en,
      has_es: alert.has_es
    }

    switch (alert.trigger_type) {
      case 'fixed_date': {
        // Single occurrence on scheduled_at
        if (!alert.scheduled_at) return
        events.push({
          ...base,
          expiration: dateKey(parseLocalDate(alert.scheduled_at)),
          rrule: null,
          recurrence_end: null
        })
        break
      }

      case 'before_expiry': {
        // Single occurrence: doc expiry minus offset_days
        if (!alert.doc_expiry || alert.offset_days == null) return
        const expiry = parseLocalDate(alert.doc_expiry)
        if (!expiry) return
        expiry.setDate(expiry.getDate() - alert.offset_days)
        events.push({
          ...base,
          expiration: dateKey(expiry),
          rrule: null,
          recurrence_end: null
        })
        break
      }

      case 'recurring': {
        // Expand rrule exactly like expandReminders does
        if (!alert.rrule || !alert.scheduled_at) return
        try {
          const parsedRule = RRule.fromString(alert.rrule)
          const startsAt = parseLocalDate(alert.scheduled_at)
          const rule = new RRule({
            ...parsedRule.origOptions,
            dtstart: startsAt
          })
          const end = alert.recurrence_end
            ? parseLocalDate(alert.recurrence_end)
            : windowEnd || new Date(startsAt.getFullYear() + 2, 0, 1)

          rule
            .all()
            .filter((date) => date >= startsAt && date <= end)
            .forEach((date) => {
              events.push({
                ...base,
                expiration: dateKey(date),
                rrule: alert.rrule,
                recurrence_end: alert.recurrence_end
              })
            })
        } catch (e) {
          console.warn('Invalid rrule on doc alert', alert.rule_id, e)
        }
        break
      }

      default:
        break
    }
  })

  return events
}

// ─── Component ────────────────────────────────────────────────────────────────
class CalendarioCustom extends React.Component {
  static contextType = authContext

  constructor(props) {
    super(props)
    const now = new Date()
    this.state = {
      year: now.getFullYear(),
      month: now.getMonth(),
      eventMap: {},
      loading: true,
      view: 'calendar', // 'calendar' | 'list'

      // panel: day event list
      panelOpen: false,
      panelDate: null, // Date object for selected day
      panelSort: 'asc', // 'asc' | 'desc'

      // detail: single event
      detailOpen: false,
      detailEvent: null
    }
  }

  componentDidMount() {
    this.fetchData()
  }

  fetchTranslation = async (templateId, locale) => {
    try {
      const data = await getTemplateById(templateId)
      if (!data?.translations) return null
      return data.translations.find((tr) => tr.locale === locale) || null
    } catch (e) {
      console.error('fetchTranslation error', e)
      return null
    }
  }

  // ── Data fetching ───────────────────────────────────────────────────────────
  fetchData = async () => {
    this.setState({ loading: true })
    try {
      const { auth } = this.context
      let raw = []

      if (
        auth.type === 'blp' ||
        auth.user.userType === 'admin_zapotal' ||
        auth.user.userType === 'external_admin'
      ) {
        const [alerts, expirations] = await Promise.all([
          getDocAlertEvents(),
          listExpirationsByBrandLabel()
        ])
        raw = [...alerts, ...expirations]

        const now = new Date()
        const twoYearsOut = new Date(
          now.getFullYear() + 2,
          now.getMonth(),
          now.getDate()
        )
        raw = raw.concat(expandAlerts(alerts, twoYearsOut))
      } else if (auth.type === 'client') {
        const mainClient = await getMainClient(auth.user.id)
        let clientList = []

        if (auth.user.userType === 'client_admin') {
          clientList = [mainClient]
        } else {
          clientList = await listAuthorizedClientsByClientUser(auth.user.id)
        }

        const results = await Promise.all(
          clientList.map((c) =>
            Promise.all([
              getDocAlertEventsByClient(c.id),
              getExpirationsByClient(c.id)
            ])
          )
        )

        const now = new Date()
        const twoYearsOut = new Date(
          now.getFullYear() + 2,
          now.getMonth(),
          now.getDate()
        )

        results.forEach(([evs, exps]) => {
          raw = raw.concat(
            expandAlerts(evs || [], twoYearsOut) || [],
            exps || []
          )
        })
      } else {
        // blp_user (non-admin)
        const clientList = await listClientsByUser(auth.user.id)
        const results = await Promise.all(
          clientList.map((c) =>
            Promise.all([
              getDocAlertEventsByClient(c.id),
              getExpirationsByClient(c.id)
            ])
          )
        )

        const now = new Date()
        const twoYearsOut = new Date(
          now.getFullYear() + 2,
          now.getMonth(),
          now.getDate()
        )

        results.forEach(([evs, exps]) => {
          raw = raw.concat(
            expandAlerts(evs || [], twoYearsOut) || [],
            exps || []
          )
        })
      }

      // Fetch and expand reminders
      const reminders = await getReminders()

      const now = new Date()
      const twoYearsOut = new Date(
        now.getFullYear() + 2,
        now.getMonth(),
        now.getDate()
      )
      const expanded = expandReminders(reminders, null, twoYearsOut)
      raw = raw.concat(expanded)

      this.setState({ eventMap: buildEventMap(raw), loading: false })
    } catch (e) {
      console.error('Calendar fetch error', e)
      this.setState({ loading: false })
    }
  }

  // ── Navigation ──────────────────────────────────────────────────────────────
  navigate = (dir) => {
    this.setState((s) => {
      let m = s.month + dir
      let y = s.year
      if (m > 11) {
        m = 0
        y++
      }
      if (m < 0) {
        m = 11
        y--
      }
      return { month: m, year: y }
    })
  }

  goToday = () => {
    const now = new Date()
    this.setState({ month: now.getMonth(), year: now.getFullYear() })
  }

  // ── Panel helpers ───────────────────────────────────────────────────────────
  openDayPanel = (date) => {
    const k = dateKey(date)
    const events = this.state.eventMap[k] || []
    if (!events.length) return
    this.setState({
      panelOpen: true,
      panelDate: date,
      panelSort: 'asc',
      detailOpen: false,
      detailEvent: null
    })
  }

  openDetail = (ev) => {
    this.setState({ detailOpen: true, detailEvent: ev })
  }

  closeAll = () => {
    this.setState({ panelOpen: false, detailOpen: false, detailEvent: null })
  }

  goToDocument = (ev) => {
    const { auth } = this.context
    if (ev.doc_id || ev.id) {
      const docId = ev.doc_id || ev.id
      if (
        auth.type === 'blp' ||
        auth.user.userType === 'admin_zapotal' ||
        auth.user.userType === 'external_admin'
      ) {
        window.open(`/client/${ev.client_id}?doc=${docId}`, '_self')
      } else {
        window.open(`/MisDocumentos?doc=${docId}`, '_self')
      }
    }
  }

  // ── Sorted events for panel ─────────────────────────────────────────────────
  getSortedPanelEvents = () => {
    const { panelDate, panelSort, eventMap } = this.state
    if (!panelDate) return []
    const k = dateKey(panelDate)
    const events = eventMap[k] || []
    if (panelSort === 'desc') {
      return [...events].sort((a, b) =>
        (b.filename || '').localeCompare(a.filename || '')
      )
    }
    return [...events].sort((a, b) =>
      (a.filename || '').localeCompare(b.filename || '')
    )
  }

  // ── Sorted events for list view (per day) ───────────────────────────────────
  getSortedListDayEvents = (events, sort) => {
    if (sort === 'alpha') {
      return [...events].sort((a, b) =>
        (a.filename || '').localeCompare(b.filename || '')
      )
    }
    return events
  }

  // ── Calendar cells ──────────────────────────────────────────────────────────
  renderCalendar() {
    const { year, month, eventMap, loading } = this.state
    const now = new Date()
    const todayK = dateKey(now)

    if (loading) {
      return (
        <div
          style={{
            textAlign: 'center',
            padding: '3rem 0',
            color: '#333',
            fontSize: 14
          }}
        >
          Loading…
        </div>
      )
    }

    const first = new Date(year, month, 1)
    const last = new Date(year, month + 1, 0)
    const startDay = first.getDay()
    const totalCells = Math.ceil((startDay + last.getDate()) / 7) * 7

    const cells = []
    for (let i = 0; i < totalCells; i++) {
      const d = new Date(year, month, 1 + (i - startDay))
      const k = dateKey(d)
      const isCurrent = d.getMonth() === month
      const isToday = k === todayK
      const events = eventMap[k] || []
      const hasEvents = events.length > 0
      const maxPills = 2

      // Detect mobile-ish width: show dots instead of pills when narrow
      // We use CSS media in S.dayCell but for pills we check container width heuristically
      const isMobile = window.innerWidth < 520

      cells.push(
        <div
          key={k}
          style={S.dayCell(isCurrent, isToday, hasEvents)}
          onClick={() => hasEvents && this.openDayPanel(d)}
          onMouseEnter={(e) => {
            if (hasEvents) e.currentTarget.style.borderColor = '#102a46'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = isToday ? '#102a46' : '#333'
          }}
        >
          <div style={S.dayNum(isToday)}>{d.getDate()}</div>

          {isMobile ? (
            hasEvents && (
              <div style={S.dotRow}>
                {events.slice(0, 4).map((ev, i) => (
                  <div key={i} style={S.dot(ev.type)} />
                ))}
                {events.length > 4 && (
                  <span style={{ fontSize: 9, color: '#333' }}>
                    +{events.length - 4}
                  </span>
                )}
              </div>
            )
          ) : (
            <>
              {events.slice(0, maxPills).map((ev, i) => (
                <div key={i} style={S.pill(ev.type)}>
                  {ev.filename}
                </div>
              ))}
              {events.length > maxPills && (
                <div style={S.moreBadge}>+{events.length - maxPills} more</div>
              )}
            </>
          )}
        </div>
      )
    }

    return (
      <>
        <div style={S.weekdayRow}>
          {DAYS_SHORT.map((d) => (
            <div key={d} style={S.weekdayCell}>
              {d}
            </div>
          ))}
        </div>
        <div style={S.grid}>{cells}</div>
      </>
    )
  }

  // ── List view ───────────────────────────────────────────────────────────────
  renderList() {
    const { eventMap, loading } = this.state

    if (loading) {
      return (
        <div
          style={{
            textAlign: 'center',
            padding: '3rem 0',
            color: '#333',
            fontSize: 14
          }}
        >
          Loading…
        </div>
      )
    }

    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const oneWeekAgo = new Date(today)
    oneWeekAgo.setDate(today.getDate() - 7)

    const days = Object.keys(eventMap)
      .filter((k) => {
        const d = parseLocalDate(k)
        return d >= oneWeekAgo
      })
      .sort()

    if (!days.length) {
      return (
        <div
          style={{
            textAlign: 'center',
            padding: '3rem 0',
            color: '#333',
            fontSize: 14
          }}
        >
          No events found.
        </div>
      )
    }

    return (
      <div style={S.listWrap}>
        {days.map((k) => {
          const d = parseLocalDate(k)
          const label = `${DAYS_SHORT[d.getDay()]}, ${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`
          return (
            <ListDayGroup
              key={k}
              label={label}
              events={eventMap[k]}
              onEventClick={(ev) => this.openDetail(ev)}
            />
          )
        })}
      </div>
    )
  }

  // ── Day event list panel ────────────────────────────────────────────────────
  renderDayPanel() {
    const { panelOpen, panelDate, panelSort } = this.state
    if (!panelOpen) return null

    const events = this.getSortedPanelEvents()
    const d = panelDate
    const label = `${DAYS_SHORT[d.getDay()]}, ${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`

    return (
      <div
        style={S.overlay}
        onClick={(e) => e.target === e.currentTarget && this.closeAll()}
      >
        <div style={S.panel}>
          <div style={S.panelHead}>
            <div style={S.panelTitle}>{label}</div>
            <button style={S.panelClose} onClick={this.closeAll}>
              ✕
            </button>
          </div>

          <div style={S.panelSortRow}>
            <span style={S.sortLabel}>Sort</span>
            <button
              style={S.sortChip(panelSort === 'asc')}
              onClick={() => this.setState({ panelSort: 'asc' })}
            >
              A → Z
            </button>
            <button
              style={S.sortChip(panelSort === 'desc')}
              onClick={() => this.setState({ panelSort: 'desc' })}
            >
              Z → A
            </button>
          </div>

          <div style={S.panelBody}>
            {events.map((ev, i) => (
              <div
                key={i}
                style={S.eventListItem}
                onClick={() => this.openDetail(ev)}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.borderColor = '#102a46')
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.borderColor = '#333')
                }
              >
                <div style={S.eventListBar(ev.type)} />
                <div style={S.eventListContent}>
                  <div style={S.eventListType(ev.type)}>
                    {ev.type === 'expiration'
                      ? 'Expiration'
                      : ev.type === 'reminder'
                        ? 'Reminder'
                        : 'Alert'}
                  </div>
                  <div style={S.eventListName}>{ev.filename}</div>
                  {ev.name && <div style={S.eventListClient}>{ev.name}</div>}
                </div>
                <span style={S.eventListChevron}>›</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  // ── Detail panel ────────────────────────────────────────────────────────────
  renderDetailPanel() {
    const { detailOpen, detailEvent, panelOpen } = this.state
    if (!detailOpen || !detailEvent) return null
    const ev = detailEvent
    const hasDoc = !!(ev.doc_id || ev.id)
    const expDate = parseLocalDate(ev.expiration)
    const dateLabel = expDate
      ? `${MONTHS[expDate.getMonth()]} ${expDate.getDate()}, ${expDate.getFullYear()}`
      : '—'

    return (
      <div
        style={S.overlay}
        onClick={(e) => e.target === e.currentTarget && this.closeAll()}
      >
        <div style={S.detailPanel}>
          <div style={S.detailHead}>
            {panelOpen && (
              <button
                style={S.backBtn}
                onClick={() =>
                  this.setState({ detailOpen: false, detailEvent: null })
                }
              >
                ‹ Back
              </button>
            )}
            <button
              style={{ ...S.panelClose, marginLeft: 'auto' }}
              onClick={this.closeAll}
            >
              ✕
            </button>
          </div>

          <div style={S.detailBody}>
            <div style={S.detailTypeBadge(ev.type)}>
              {ev.type === 'expiration'
                ? 'Expiration'
                : ev.type === 'reminder'
                  ? 'Reminder'
                  : 'Alert'}
            </div>

            <div style={S.detailTitle}>{ev.filename}</div>

            {ev.name && (
              <div style={S.detailRow}>
                <div style={S.detailLabel}>Client</div>
                <div style={S.detailValue}>{ev.name}</div>
              </div>
            )}

            <div style={S.detailRow}>
              <div style={S.detailLabel}>Date</div>
              <div style={S.detailValue}>{dateLabel}</div>
            </div>

            {ev.type !== 'expiration' && ev.template_id && (
              <div style={{ marginTop: '1rem' }}>
                <TemplatePreview
                  templateId={ev.template_id}
                  has_en={ev.has_en}
                  has_es={ev.has_es}
                  fetchTranslation={this.fetchTranslation}
                />
              </div>
            )}

            {ev.extension && (
              <div style={S.detailRow}>
                <div style={S.detailLabel}>Type</div>
                <div style={S.detailValue}>{ev.extension.toUpperCase()}</div>
              </div>
            )}

            {hasDoc && (
              <button style={S.goDocBtn} onClick={() => this.goToDocument(ev)}>
                Go to document →
              </button>
            )}
          </div>
        </div>
      </div>
    )
  }

  render() {
    const { year, month, view } = this.state

    return (
      <div style={S.wrap}>
        {/* Header */}
        <div style={S.header}>
          <div style={S.monthLabel}>
            {MONTHS[month]} <span style={S.yearSpan}>{year}</span>
          </div>

          <div style={S.headerRight}>
            {/* Nav */}
            {view === 'calendar' && (
              <div style={S.navGroup}>
                <button style={S.navBtn} onClick={() => this.navigate(-1)}>
                  ←
                </button>
                <button style={S.todayBtn} onClick={this.goToday}>
                  Today
                </button>
                <button style={S.navBtn} onClick={() => this.navigate(1)}>
                  →
                </button>
              </div>
            )}

            {/* View toggle */}
            <div style={S.viewToggle}>
              <button
                style={S.viewBtn(view === 'calendar')}
                onClick={() => this.setState({ view: 'calendar' })}
              >
                Calendar
              </button>
              <button
                style={S.viewBtn(view === 'list')}
                onClick={() => this.setState({ view: 'list' })}
              >
                List
              </button>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div style={S.legend}>
          <div style={S.legendItem}>
            <div style={S.legendDot('#A32D2D')} />
            Expiration
          </div>
          <div style={S.legendItem}>
            <div style={S.legendDot('#185FA5')} />
            Alert
          </div>
          <div style={S.legendItem}>
            <div style={S.legendDot('#0F6E56')} />
            Reminder
          </div>
        </div>

        {/* Main content */}
        {view === 'calendar' ? this.renderCalendar() : this.renderList()}

        {/* Overlays */}
        {this.renderDayPanel()}
        {this.renderDetailPanel()}
      </div>
    )
  }
}

// ─── List day group (stateful for per-day sort) ───────────────────────────────
class ListDayGroup extends React.Component {
  state = { sort: 'asc' }

  render() {
    const { label, events, onEventClick } = this.props
    const { sort } = this.state
    const sorted =
      sort === 'desc'
        ? [...events].sort((a, b) =>
            (b.filename || '').localeCompare(a.filename || '')
          )
        : [...events].sort((a, b) =>
            (a.filename || '').localeCompare(b.filename || '')
          )

    return (
      <div style={S.listDayGroup}>
        <div style={S.listDayHeader}>
          <div style={S.listDayTitle}>{label}</div>
          <button
            style={S.listSortBtn}
            onClick={() =>
              this.setState({ sort: sort === 'asc' ? 'desc' : 'asc' })
            }
          >
            {sort === 'asc' ? 'Sort A → Z' : 'Sort Z → A'}
          </button>
        </div>

        {sorted.map((ev, i) => (
          <div
            key={i}
            style={S.listEventRow}
            onClick={() => onEventClick(ev)}
            onMouseEnter={(e) =>
              (e.currentTarget.style.borderColor = '#185FA5')
            }
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = '#fff')}
          >
            <div style={S.listEventBar(ev.type)} />
            <div style={S.listEventInfo}>
              <div style={S.listEventName}>{ev.filename}</div>
              {ev.name && <div style={S.listEventClient}>{ev.name}</div>}
            </div>
            <div style={S.listEventType(ev.type)}>
              {ev.type === 'expiration'
                ? 'Expiration'
                : ev.type === 'reminder'
                  ? 'Reminder'
                  : 'Alert'}
            </div>
          </div>
        ))}
      </div>
    )
  }
}

export default withTranslation()(CalendarioCustom)
